import { createTheme } from '@mui/material/styles';

/**
 * Dark Professional Theme
 * Sleek dark design with vibrant accents
 */

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: 'rgba(255, 255, 255, 0.5)',
      light: 'rgba(255, 255, 255, 0.5)',
      dark: 'rgba(255, 255, 255, 0.15)',
      contrastText: '#000000',
    },
    secondary: {
      main: '#06b6d4',      // Cyan
      light: '#22d3ee',
      dark: '#0891b2',
      contrastText: '#ffffff',
    },
    background: {
      default: '#0a0a0a',   // Almost black
      paper: '#141414',     // Dark gray
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.6)',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    success: {
      main: '#22c55e',
      light: '#4ade80',
      dark: '#16a34a',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: 14,
    h1: {
      fontWeight: 800,
      fontSize: '2.75rem',
      letterSpacing: '-0.02em',
      color: '#ffffff',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.25rem',
      letterSpacing: '-0.01em',
      color: '#ffffff',
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.875rem',
      color: '#ffffff',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      color: '#ffffff',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      color: '#ffffff',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      color: '#ffffff',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.5)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.5), 0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0a0a0a',
          scrollbarWidth: 'thin',
          scrollbarColor: '#27272a #0a0a0a',
        },
        '*::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '*::-webkit-scrollbar-track': {
          background: '#0a0a0a',
        },
        '*::-webkit-scrollbar-thumb': {
          background: '#27272a',
          borderRadius: '4px',
          '&:hover': {
            background: '#3f3f46',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#141414',
          backgroundImage: 'linear-gradient(to bottom right, rgba(185, 28, 28, 0.03), rgba(185, 28, 28, 0.02))',
          borderRadius: 12,
          border: '1px solid rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.2)',
            transform: 'translateY(-2px)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          fontWeight: 600,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: 'rgba(185, 28, 28, 0.08)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            transition: 'all 0.2s ease',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                borderWidth: '2px',
              },
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.15)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.1)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.15)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-focused': {
            color: 'rgba(255, 255, 255, 0.9)',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-focused': {
            color: 'rgba(255, 255, 255, 0.9)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(10, 10, 10, 0.8)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0a0a0a',
          backgroundImage: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.02), transparent)',
          borderRight: '1px solid rgba(255, 255, 255, 0.05)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          border: '1px solid',
          alignItems: 'center',
        },
        icon: {
          alignItems: 'center',
          paddingTop: 0,
          paddingBottom: 0,
        },
        message: {
          paddingTop: 0,
          paddingBottom: 0,
        },
        action: {
          alignItems: 'center',
          paddingTop: 0,
          paddingBottom: 0,
          marginRight: 0,
        },
        standardError: {
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderColor: 'rgba(239, 68, 68, 0.3)',
          color: '#fca5a5',
        },
        standardSuccess: {
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderColor: 'rgba(34, 197, 94, 0.3)',
          color: '#86efac',
        },
        standardInfo: {
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderColor: 'rgba(59, 130, 246, 0.3)',
          color: '#93c5fd',
        },
        standardWarning: {
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          borderColor: 'rgba(245, 158, 11, 0.3)',
          color: '#fcd34d',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
        filled: {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: '#141414',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderRadius: 8,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.05)',
        },
        head: {
          fontWeight: 700,
          color: '#ffffff',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          letterSpacing: '0.05em',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 0',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            borderLeft: '3px solid rgba(255, 255, 255, 0.6)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            },
          },
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        },
        indicator: {
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.6)',
          fontWeight: 500,
          textTransform: 'none',
          minHeight: 48,
          '&.Mui-selected': {
            color: '#ffffff',
          },
          '&:hover': {
            color: 'rgba(255, 255, 255, 0.8)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          background: '#141414',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
        invisible: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          fontWeight: 600,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.9)',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },
    // MUI X Date Pickers styling
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1) !important',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.15) !important',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.5) !important',
                borderWidth: '2px',
              },
            },
          },
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.1) !important',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.15) !important',
          },
          '&.Mui-focused': {
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.5) !important',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    // Slider - white theme (used in PaymentPage, etc.)
    MuiSlider: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
        },
        thumb: {
          backgroundColor: '#ffffff',
          '&:hover, &.Mui-focusVisible, &.Mui-active': {
            boxShadow: '0 0 0 8px rgba(255, 255, 255, 0.16)',
          },
        },
        track: {
          background: 'rgba(255, 255, 255, 0.7)',
        },
        rail: {
          color: 'rgba(255, 255, 255, 0.3)',
        },
        mark: {
          color: 'rgba(255, 255, 255, 0.5)',
        },
        markLabel: {
          color: '#94a3b8',
          fontSize: '0.75rem',
        },
      },
    },
    // Skeleton - glass effect
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
        },
      },
    },
    // IconButton - subtle hover
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
      },
    },
    // List - clean padding
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    // Tooltip - dark glass style
    MuiTooltip: {
      // Всплывашка висит в <body>. По умолчанию popper.js ставит ей position: absolute, и тултип,
      // уехавший вместе с якорем за нижний край окна (листаешь таблицу вверх — строка едет вниз
      // под курсором), на миг удлиняет страницу: у неё мигает скролл. fixed в высоту документа
      // не входит. Из темы доезжает до каждого тултипа, пока тот не задаёт свои
      // slotProps.popper.popperOptions — тогда они заменяют эти целиком (docs/ui-patterns.md §7).
      defaultProps: {
        slotProps: {
          popper: {
            popperOptions: { strategy: 'fixed' },
          },
        },
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(20, 20, 20, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          fontSize: '0.8125rem',
          // Перенос строки в тексте подсказки остаётся переносом: подсказка-список (например, что
          // делает каждый режим синтеза) иначе слипается в абзац. Однострочные не меняются.
          whiteSpace: 'pre-line',
        },
        arrow: {
          color: 'rgba(20, 20, 20, 0.95)',
        },
      },
    },
  } as any,
});
